/**
 * An empty immutable map of event listener to scopes, used for a mismatch in
 * the {@codelink _eventListeners} map.
 *
 * @const
 * @type {Map<function (*), Set<?Object>>}
 */
export const EMPTY_MAP = Object.freeze(new Map());

export default class EventEmitter {
	constructor() {
		/**
		 * Map of event names to a map of event listeners to a set of scopes to
		 * which the event listener should be bound when being executed due to
		 * the event.
		 *
		 * @type {Map<string, Map<function(*), Set<?Object>>>}
		 */
		this._eventListeners = new Map();
	}

	clear() {
		this._eventListeners.clear();

		return this;
	}

	listen(event, listener) {
		if (!(listener instanceof Function)) {
			throw new GenericError(
				`The listener must be a function, ${listener} provided.`
			);
		}

		if (!this._eventListeners.has(event)) {
			this._createNewEvent(event);
		}

		const listeners = this._getListenersOf(event);

		if (!listeners.has(listener)) {
			this._createNewListener(event, listener);
		}

		return this;
	}

	unlisten(event, listener) {
		const listeners = this._getListenersOf(event);
		listeners.delete(listener);

		if (!listeners.size) {
			this._eventListeners.delete(event);
		}

		return this;
	}

	fire(event, data) {
		const listeners = this._getListenersOf(event);

		for (const listener of listeners) {
			listener(data);
		}

		return this;
	}

	/**
	 * Create new Map storage of listeners for the specified event.
	 *
	 * @param {string} event The name of the event.
	 */
	_createNewEvent(event) {
		let listeners = new Set();
		this._eventListeners.set(event, listeners);
	}

	/**
	 * Create new Set storage of scopes for the specified event and listener.
	 *
	 * @param {string} event The name of the event.
	 * @param {function(*)} listener The event listener.
	 */
	_createNewListener(event, listener) {
		this._eventListeners.get(event).add(listener);
	}

	/**
	 * Retrieves the map of event listeners to scopes they are bound to.
	 *
	 * @param {string} event The name of the event.
	 * @return {Map<function(*), Set<?Object>>} A map of event listeners to the
	 *         scopes in which they should be executed. The returned map is an
	 *         unmodifiable empty map if no listeners are registered for the
	 *         event.
	 */
	_getListenersOf(event) {
		if (this._eventListeners.has(event)) {
			return this._eventListeners.get(event);
		}

		return EMPTY_MAP;
	}
}
